/**
 * Configuration shape and built-in defaults.
 *
 * ⛔ THIS FILE IS THE ONLY SOURCE OF DEFAULTS. The shipped `sibyl.toml` is generated from it by
 * `sibyl init`, so the documentation and the behaviour cannot drift apart. A hand-maintained
 * default file that disagrees with the code is how a user ends up configuring something that was
 * never read.
 *
 * Full prose and the reason behind each default: docs/spec/config.md
 */

import type { Severity } from "../types/finding.js"

export const SCHEMA_VERSION = 1

export type Backend = "claude_cli" | "anthropic_api"
export type BackendSelector = Backend | "auto" | "none"
export type EffortLevel = "low" | "medium" | "high" | "xhigh" | "max"
export type CommitStatusState = "success" | "failure" | "pending" | "none"

export interface ServerConfig {
  /** ⛔ Loopback only. This surface can post to a shared repo and has no authentication. */
  bind: string
  port: number
  /** Must be flipped explicitly before `bind` may be anything else, and warns on every boot. */
  allowNonLoopback: boolean
  openBrowser: boolean
  shutdownGraceMs: number
  theme: {
    mode: "dark" | "light" | "system"
    accent: string
    density: "comfortable" | "compact"
    glassAlpha: number
    blurPx: number
  }
}

export interface ClaudeCliConfig {
  bin: string
  /** Pinned by full id, never an alias — an alias moving would silently change stored provenance. */
  model: string
  fallbackModel: string[]
  effort: EffortLevel
  outputFormat: "json" | "stream-json"
  jsonSchemaEnforce: boolean
  permissionMode: string
  /** ⛔ Nobody is at the keyboard; anything that would prompt is denied, not left hanging. */
  permissionPrompts: "host" | "none"
  /** ⛔ The most load-bearing default in the file. A reviewer reads the branch; it does not run it. */
  tools: string[]
  addDir: string[]
  restricted: boolean
  bare: boolean
  strictMcpConfig: boolean
  settingSources: string[]
  sessionPersistence: boolean
  /** ⚠️ 0 means uncapped, which is what subscription billing implies. */
  maxBudgetUsd: number
  timeoutMs: number
  maxProcesses: number
  extraArgs: string[]
}

export interface AnthropicApiConfig {
  model: string
  effort: EffortLevel
  /** ⛔ `budget_tokens` does not exist on this model family and 400s. Deliberately no knob for it. */
  thinking: "adaptive" | "disabled"
  thinkingDisplay: "omitted" | "summarized"
  maxTokens: number
  stream: boolean
  baseUrl: string
  maxRetries: number
  timeoutMs: number
  promptCache: boolean
  /** The shared corpus is reused across dimensions and PRs; 1h pays back after two reads. */
  promptCacheTtl: "5m" | "1h"
  /** ⛔ The NAME of an environment variable, never a value. */
  apiKeyName: string
}

export interface DimensionConfig {
  id: string
  title: string
  /** What the harness routes on. Written for a machine, not a person. */
  description: string
  enabled: boolean
  promptFile?: string
  prompt?: string
  /** Keep the shipped prompt and add a house rule, rather than forking it and missing every fix. */
  promptAppend: string
  backend: BackendSelector | "inherit"
  model: string | "inherit"
  effort: EffortLevel | "inherit"
  severityCeiling: Severity
  maxFindings: number
  /**
   * ⛔ Tells the model NOT to self-filter. Models follow "only report high-severity" literally:
   * they find the bug, judge it below the stated bar, and drop it. Filtering belongs in triage,
   * which is auditable and tunable; a silent drop inside the model is neither.
   */
  reportEverything: boolean
  include: string[]
  exclude: string[]
  /** ⛔ Tie-break ordering only. Never summed — adding verdicts invents authority. */
  weight: number
}

export interface SeverityAction {
  comment: boolean
  suggestion: boolean | "optional"
  commitStatus: CommitStatusState
  blocksMerge?: boolean
  rollup?: boolean
  dropIfNoSuggestion?: boolean
}

export interface PathOverride {
  match: string[]
  /** Promotion beats suppression when a floor and a ceiling cross. */
  severityFloor?: Severity
  severityCeiling?: Severity
  dimensions?: string[]
  /** Review these even when diff-size limits would have truncated the run. */
  alwaysReview?: boolean
}

export interface SibylConfig {
  schemaVersion: number
  server: ServerConfig
  backend: {
    default: BackendSelector
    probeTimeoutMs: number
    /**
     * How long an availability probe is reused. Re-probing per dimension is five process spawns to
     * answer one question. "process" caches for the lifetime of the run; "off" probes every time.
     */
    probeCache: "process" | "off"
    /** ⛔ Never silently degrade to a weaker backend and present the result as a full review. */
    onUnavailable: "fail" | "fallback" | "skip"
    fallbackOrder: Backend[]
    claudeCli: ClaudeCliConfig
    anthropicApi: AnthropicApiConfig
  }
  dimensions: DimensionConfig[]
  review: { dimensionOrder: string[]; runDisabled: boolean; failFast: boolean }
  severity: {
    levels: Severity[]
    default: Severity
    requireConfidence: boolean
    actions: Record<Severity, SeverityAction>
    preExisting: {
      enabled: boolean
      detect: "cited_lines_outside_diff" | "blame_age" | "both"
      demoteTo: Severity | "keep"
      comment: boolean
      commitStatus: CommitStatusState
      surfaceInUi: boolean
      allowPromote: boolean
    }
  }
  suggestion: {
    maxLines: number
    singleHunkOnly: boolean
    /** ⛔ GitHub silently drops a suggestion anchored outside the diff. Refusing is honest. */
    mustBeWithinDiff: boolean
  }
  paths: {
    include: string[]
    exclude: string[]
    caseSensitive: boolean
    /** ⛔ A symlink out of the clone is a path escape. */
    followSymlinks: boolean
    respectGitignore: boolean
    /** Above this a file is excluded AND named in the report, never silently skipped. */
    maxFileBytes: number
    overrides: PathOverride[]
  }
  diff: {
    /** Well above git's 3: three lines reliably hides the guard clause that makes it not a bug. */
    contextLines: number
    wholeFileUnderLines: number
    maxChangedFiles: number
    maxTotalBytes: number
    /** ⛔ truncate/skip produce a review that LOOKS complete over a diff that was not read. */
    onExceed: "chunk" | "truncate" | "skip" | "fail"
    chunkTargetTokens: number
    chunkMaxTokens: number
    chunkOverlapLines: number
    chunkStrategy: "by_file" | "by_directory" | "by_size"
    ignoreWhitespace: boolean
  }
  concurrency: {
    dimensions: number
    chunksPerDimension: number
    maxModelCalls: number
    nice: number
    retryBackoffMs: number[]
    githubRpm: number
  }
  triage: {
    /** ⛔ THE MASTER SWITCH. While true, nothing posts without a person, whatever else says. */
    requireHuman: boolean
    db: string
    groupBy: "file" | "dimension" | "severity" | "none"
    dedupe: boolean
    /** A dismissal survives a force-push if the cited code survives it. */
    carryForward: boolean
    carryForwardMatch: "line_content" | "line_number" | "none"
    retainDismissed: string
  }
  comments: {
    /** Past ~25 inline comments a review is read as noise and closed. */
    maxPerReview: number
    maxPerFile: number
    maxPerDimension: number
    maxPerLine: number
    overflow: "rollup" | "drop" | "fail"
    /** ⚠️ A displayed number reads as a measurement. It is a self-report. Kept for triage only. */
    includeConfidence: boolean
    summaryComment: boolean
    /** "Reviewed, found nothing" is a RESULT. Silence looks like a crash. */
    summaryOnEmpty: boolean
    signature: string
    /** ⛔ @mentions re-fire on every edit. */
    mentionUsers: boolean
  }
  post: {
    enabled: boolean // LOCK 1
    dryRun: boolean // LOCK 2
    confirmToken: string
    /** ⛔ A machine does not approve. Also: our token is the PR author, who cannot approve. */
    reviewEvent: "COMMENT"
    batch: boolean
    updateExisting: boolean
    /** If the head sha moved between review and post, abort. */
    onConflict: "abort" | "repost" | "force"
    commitStatus: {
      enabled: boolean
      context: string
      targetUrlBase: string
      onNoFindings: CommitStatusState
      /** ⛔ A broken reviewer must not fail someone's build. Our outage is not their defect. */
      onError: CommitStatusState
    }
  }
  repo: {
    slug: string
    cloneRoot: string
    refTemplate: string
    fallbackRef: string
    singleBranch: boolean
    depth: number
    reuse: boolean
    /**
     * How long a clone may sit idle before it is removed. ⭐ `"0"` disables pruning: the safe
     * reading of "no window configured" is delete nothing, never delete everything.
     *
     * ⚠️ Documented in docs/spec/config.md since W1 and absent from this file until W3 needed it —
     * a user copying the shipped default file got a key the loader knew nothing about.
     */
    pruneAfter: string
    /** ⛔ Reviewing the operator's live tree. Exists only because everything is configurable. */
    allowInPlaceReview: boolean
    /** ⛔ Never `git worktree prune` — it deregisters other sessions' worktrees. */
    neverPruneWorktrees: boolean
  }
  secrets: {
    store: string
    requireMode600: boolean
    keys: string[]
    redactMinLen: number
    /** ⛔ Not configurable to anything else. Any other value is a startup error. */
    readback: "never"
    fingerprintChars: number
    ghCliFallback: boolean
  }
  validation: {
    unknownKeys: "error" | "warn" | "ignore"
    missingPrompt: "error" | "warn"
    badGlob: "error" | "warn"
    emptyDimensions: "error" | "warn"
    printResolved: "always" | "on_change" | "never"
  }
}

/**
 * Applied to every dimension, including one a user adds.
 *
 * ⛔ A brand-new [[dimension]] block must start from these, not from an empty object. Otherwise the
 * array fields are undefined and validation crashes on a user's first custom dimension — which is
 * exactly what the test suite caught.
 */
export const DIMENSION_DEFAULTS = {
  enabled: true,
  promptAppend: "",
  backend: "inherit",
  model: "inherit",
  effort: "inherit",
  severityCeiling: "medium",
  maxFindings: 20,
  reportEverything: true,
  include: [],
  exclude: [],
  weight: 1.0,
} satisfies Partial<DimensionConfig>

/** The team's existing five, unchanged. Order here is execution order. */
export const DEFAULT_DIMENSIONS: DimensionConfig[] = [
  {
    ...DIMENSION_DEFAULTS,
    id: "adversarial",
    title: "Adversarial",
    description: "Hunts for inputs, orderings and failure modes that break this change on purpose.",
    promptFile: "prompts/adversarial.md",
    severityCeiling: "high",
    maxFindings: 25,
  },
  {
    ...DIMENSION_DEFAULTS,
    id: "security",
    title: "Security",
    description:
      "Tenant isolation, permission gating, visibility rules, secret handling, what leaks to whom.",
    promptFile: "prompts/security.md",
    severityCeiling: "high",
    maxFindings: 25,
  },
  {
    ...DIMENSION_DEFAULTS,
    id: "code_quality",
    title: "Code Quality",
    description:
      "Does it do what it says, does it break a caller elsewhere, will the naming mislead the next reader.",
    promptFile: "prompts/code-quality.md",
    // ⭐ Style must not be able to fail a build.
    severityCeiling: "medium",
    maxFindings: 20,
  },
  {
    ...DIMENSION_DEFAULTS,
    id: "performance",
    title: "Performance",
    description:
      "Work added to a hot path: N+1 queries, re-renders, sync I/O, unbounded growth at real data volumes.",
    promptFile: "prompts/performance.md",
    severityCeiling: "high",
    maxFindings: 25,
  },
  {
    ...DIMENSION_DEFAULTS,
    id: "necessity",
    title: "Necessity",
    description: "Whether this change needs to exist and whether it is larger than the problem.",
    promptFile: "prompts/necessity.md",
    // ⭐ "You didn't need to write this" is never a merge blocker from a machine.
    severityCeiling: "medium",
    maxFindings: 10,
  },
]

const XDG = {
  config: process.env["XDG_CONFIG_HOME"] ?? "~/.config",
  data: process.env["XDG_DATA_HOME"] ?? "~/.local/share",
  state: process.env["XDG_STATE_HOME"] ?? "~/.local/state",
}

export const DEFAULTS: SibylConfig = {
  schemaVersion: SCHEMA_VERSION,
  server: {
    bind: "127.0.0.1",
    port: 4190,
    allowNonLoopback: false,
    openBrowser: true,
    shutdownGraceMs: 5000,
    theme: { mode: "dark", accent: "blue", density: "comfortable", glassAlpha: 0.72, blurPx: 14 },
  },
  backend: {
    default: "auto",
    probeTimeoutMs: 15000,
    probeCache: "process",
    onUnavailable: "fail",
    fallbackOrder: ["claude_cli", "anthropic_api"],
    claudeCli: {
      bin: "claude",
      model: "claude-opus-5",
      // ⛔ NOT EMPTY, AND NOT DECORATION. Measured 2026-09-15 against the installed CLI (2.1.272)
      // on a live review of a real pull request: every one of five dimensions came back `is_error`
      // with "Opus 5's safeguards flagged this message" over an ordinary code diff, so the shipped
      // default could not complete a single review. `--fallback-model` is passed to the CLI as well,
      // but that only covers an OVERLOADED model and a refusal is not an overload. The adapter
      // retries once here and stamps `modelFallbackFrom` on the result, so the report names the
      // model that actually answered rather than the one that was asked for.
      fallbackModel: ["claude-sonnet-5"],
      effort: "high",
      outputFormat: "json",
      jsonSchemaEnforce: true,
      permissionMode: "manual",
      permissionPrompts: "none",
      tools: ["Read", "Grep", "Glob"],
      addDir: [],
      restricted: true,
      bare: false,
      strictMcpConfig: true,
      settingSources: [],
      sessionPersistence: false,
      maxBudgetUsd: 0,
      timeoutMs: 900_000,
      maxProcesses: 3,
      extraArgs: [],
    },
    anthropicApi: {
      // ⛔ AN API MODEL ID, WHICH IS NOT THE SAME NAMESPACE AS THE CLI'S. The `claude` CLI accepts
      // aliases the Messages API does not; copying one across is how a backend that is configured
      // correctly still fails at the wire. This one is verified against the live API.
      model: "claude-opus-4-8",
      effort: "high",
      thinking: "adaptive",
      thinkingDisplay: "omitted",
      maxTokens: 32_000,
      stream: true,
      baseUrl: "",
      maxRetries: 2,
      timeoutMs: 900_000,
      promptCache: true,
      promptCacheTtl: "1h",
      apiKeyName: "ANTHROPIC_API_KEY",
    },
  },
  dimensions: DEFAULT_DIMENSIONS,
  review: { dimensionOrder: [], runDisabled: false, failFast: false },
  severity: {
    levels: ["high", "medium", "low"],
    default: "medium",
    requireConfidence: true,
    actions: {
      // ⭐ A HIGH usually needs a human to choose the fix; a one-click patch on a security bug is
      // how a wrong fix gets merged fast.
      high: { comment: true, suggestion: false, commitStatus: "failure", blocksMerge: true },
      medium: { comment: true, suggestion: "optional", commitStatus: "none", blocksMerge: false },
      // ⭐ LOW gets no comment of its own. A wall of nits is how a review gets closed unread.
      low: {
        comment: false,
        suggestion: "optional",
        commitStatus: "none",
        rollup: true,
        dropIfNoSuggestion: true,
      },
    },
    preExisting: {
      enabled: true,
      detect: "cited_lines_outside_diff",
      demoteTo: "low",
      // ⛔ A PR author is not accountable for code they did not touch.
      comment: false,
      commitStatus: "none",
      surfaceInUi: true,
      allowPromote: true,
    },
  },
  suggestion: { maxLines: 12, singleHunkOnly: true, mustBeWithinDiff: true },
  paths: {
    include: ["**/*"],
    exclude: [
      "**/node_modules/**",
      "**/vendor/**",
      "**/dist/**",
      "**/build/**",
      "**/.next/**",
      "**/*.min.js",
      "**/*.map",
      "**/*.lock",
      "**/package-lock.json",
      "**/pnpm-lock.yaml",
      "**/yarn.lock",
      "**/*.snap",
      "**/__snapshots__/**",
      "**/*.svg",
      "**/*.png",
      "**/*.jpg",
      "**/*.gif",
      "**/*.webp",
      "**/*.pdf",
      "**/*.ico",
      "**/*.pb.go",
      "**/*_pb2.py",
      "**/*.generated.*",
      "**/generated/**",
    ],
    caseSensitive: false,
    followSymlinks: false,
    respectGitignore: true,
    maxFileBytes: 262_144,
    overrides: [
      // A bad migration is not recoverable by a revert; that asymmetry deserves a floor.
      { match: ["**/migrations/**", "**/*.sql"], severityFloor: "high" },
      {
        match: ["**/*.test.ts", "**/*.spec.*", "**/tests/**", "**/__tests__/**"],
        severityCeiling: "low",
        dimensions: ["adversarial", "security"],
      },
      {
        match: ["**/auth/**", "**/*auth*", "**/crypto/**", "**/.github/workflows/**"],
        severityFloor: "medium",
        dimensions: ["security", "adversarial"],
        alwaysReview: true,
      },
    ],
  },
  diff: {
    contextLines: 12,
    wholeFileUnderLines: 400,
    maxChangedFiles: 300,
    maxTotalBytes: 4_194_304,
    onExceed: "chunk",
    chunkTargetTokens: 60_000,
    chunkMaxTokens: 90_000,
    chunkOverlapLines: 20,
    chunkStrategy: "by_file",
    // ⚠️ Whitespace CAN be the bug — Python, YAML, heredocs, Makefiles.
    ignoreWhitespace: false,
  },
  concurrency: {
    // ⭐ Not 5: five simultaneous processes is the wrong shape on a laptop also running a dev server.
    dimensions: 3,
    chunksPerDimension: 2,
    maxModelCalls: 6,
    nice: 5,
    retryBackoffMs: [1000, 4000, 15000],
    githubRpm: 60,
  },
  triage: {
    requireHuman: true,
    db: `${XDG.data}/sibyl/triage.sqlite`,
    groupBy: "file",
    dedupe: true,
    carryForward: true,
    carryForwardMatch: "line_content",
    retainDismissed: "90d",
  },
  comments: {
    maxPerReview: 25,
    maxPerFile: 6,
    maxPerDimension: 8,
    maxPerLine: 1,
    overflow: "rollup",
    includeConfidence: false,
    summaryComment: true,
    summaryOnEmpty: true,
    signature: "<!-- sibyl:{review_id}:{finding_id} -->",
    mentionUsers: false,
  },
  post: {
    enabled: false,
    dryRun: true,
    confirmToken: "",
    reviewEvent: "COMMENT",
    batch: true,
    updateExisting: true,
    onConflict: "abort",
    commitStatus: {
      enabled: true,
      // ⛔ THIS STRING IS THE GATE. A branch rule requires a check BY NAME, so this value and the
      // `required_status_checks` context on the protected branch have to be the same literal or the
      // gate never resolves and the merge stays blocked with no explanation. It was `sibyl/review`
      // while the shipped workflow template overrode it to `sibyl` — so the default and the
      // documented setup disagreed by construction, and following either one alone produced a
      // pull request that could not be merged. One name, and it is the plain one.
      context: "sibyl",
      // ⚠️ Empty on purpose — a 127.0.0.1 link in a shared PR is dead for everyone but you.
      targetUrlBase: "",
      onNoFindings: "success",
      onError: "none",
    },
  },
  repo: {
    slug: "",
    cloneRoot: `${XDG.data}/sibyl/repos`,
    refTemplate: "refs/pull/{pr}/merge",
    fallbackRef: "refs/pull/{pr}/head",
    singleBranch: true,
    depth: 50,
    reuse: true,
    pruneAfter: "14d",
    allowInPlaceReview: false,
    neverPruneWorktrees: true,
  },
  secrets: {
    store: `${XDG.config}/sibyl/credentials.env`,
    requireMode600: true,
    keys: ["GITHUB_TOKEN", "GH_TOKEN", "ANTHROPIC_API_KEY", "CLAUDE_CODE_OAUTH_TOKEN"],
    redactMinLen: 12,
    readback: "never",
    fingerprintChars: 4,
    ghCliFallback: true,
  },
  validation: {
    unknownKeys: "error",
    missingPrompt: "error",
    badGlob: "error",
    emptyDimensions: "error",
    printResolved: "on_change",
  },
}

/** Patterns that must never appear as a value in a config file. See docs/spec/config.md §5. */
export const CREDENTIAL_SHAPES: readonly RegExp[] = [
  /\bghp_[A-Za-z0-9]{20,}/,
  /\bgithub_pat_[A-Za-z0-9_]{20,}/,
  /\bsk-ant-[A-Za-z0-9_-]{20,}/,
  /\bsk_[A-Za-z0-9]{20,}/,
  /\bre_[A-Za-z0-9]{20,}/,
  /\b[0-9a-f]{40}\b/,
]

/** Loopback check that does not treat `0.0.0.0` or an outside address as safe. */
export function isLoopback(bind: string): boolean {
  return bind === "127.0.0.1" || bind === "::1" || bind === "localhost" || bind.startsWith("127.")
}
